// Shared helpers for the Appointment Chair Conflict Prevention System.
//
// These utilities are intentionally additive: they layer chair-capacity rules
// (Rule 1) and roster-vs-bench semantics on top of the existing
// `unavailableTimeSlots/{date}` document shape (`{ [dentistUid]: string[] }`)
// without altering the underlying booking logic in
// `PatientAppointmentBooking.jsx` or the slot persistence in
// `ManageAppointment.jsx`.

import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../backend/firebaseConfig";

export const CHAIR_SETTINGS_DOC_PATH = ["clinicScheduling", "settings"];
export const CHAIR_SWAPS_COLLECTION = "chairSwaps";

export const DEFAULT_CHAIR_SETTINGS = Object.freeze({
  chairCount: 0,
  rosterDentistIds: [],
});

// Subscribe to the clinic's chair-capacity configuration document. The callback
// is always invoked with a normalized object (never undefined) so consumers can
// avoid null checks.
export const subscribeToChairSettings = (callback) => {
  if (typeof callback !== "function") {
    return () => {};
  }
  const ref = doc(db, ...CHAIR_SETTINGS_DOC_PATH);
  return onSnapshot(
    ref,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() || {};
        callback({
          chairCount:
            typeof data.chairCount === "number" && data.chairCount >= 0
              ? data.chairCount
              : 0,
          rosterDentistIds: Array.isArray(data.rosterDentistIds)
            ? data.rosterDentistIds.filter((id) => typeof id === "string")
            : [],
        });
      } else {
        callback({ ...DEFAULT_CHAIR_SETTINGS });
      }
    },
    () => {
      callback({ ...DEFAULT_CHAIR_SETTINGS });
    }
  );
};

export const isDentistOnRoster = (dentistUid, settings) => {
  if (!dentistUid || !settings || !Array.isArray(settings.rosterDentistIds)) {
    return false;
  }
  return settings.rosterDentistIds.includes(dentistUid);
};

export const isBlockingAppointmentStatus = (status) => {
  const normalized = (status || "").toString().trim().toLowerCase();
  return normalized !== "cancelled" && normalized !== "canceled" && normalized !== "completed";
};

// Returns true when chair-conflict prevention is currently configured.
// When chairCount or roster is missing we fail open (no extra restriction)
// so existing clinics keep working until staff configure chairs.
export const isChairCapacityConfigured = (settings) => {
  if (!settings) return false;
  const count = Number(settings.chairCount);
  return (
    Number.isFinite(count) &&
    count > 0 &&
    Array.isArray(settings.rosterDentistIds) &&
    settings.rosterDentistIds.length > 0
  );
};

// Centralized compatibility rule for the legacy `hasScheduleSetup` behavior.
// - When chair config is enabled, chair roster is the source of truth:
//   non-roster dentists default unavailable, roster dentists do not.
// - When chair config is disabled, preserve legacy behavior:
//   dentists with hasScheduleSetup === false default unavailable.
export const shouldDefaultUnavailableByLegacySetup = ({
  dentistUid,
  settings,
  hasScheduleSetup,
}) => {
  if (isChairCapacityConfigured(settings)) {
    return !isDentistOnRoster(dentistUid, settings);
  }
  return hasScheduleSetup === false;
};

// Determine whether a given dentist is "effectively unavailable" for a slot on
// a date, layering the chair-capacity rules on top of any persisted
// unavailableTimeSlots data.
//
// - dentistUnavailableSlots: array of slot strings already blocked for the
//   dentist by `unavailableTimeSlots/{date}[dentistUid]`.
// - settings: chair settings (pass null to disable chair semantics)
//
// Bench dentists (not on roster) default to BLOCKED for every slot unless the
// slot has been explicitly opened (slot label NOT present in their array).
// However we cannot know "explicit open" from absence alone, so we rely on
// callers having recorded swap exceptions: the caller's persisted array still
// holds blocked slots; an absent slot for a bench dentist means "swapped in".
//
// To stay safe for clinics that have not yet adopted swap-aware persistence
// we ONLY enforce bench-blocking when chairs are configured AND the dentist
// has a recorded slots array (meaning swap state has been authored at least
// once). This prevents accidentally hiding all dentists for unconfigured
// clinics.
function slotLabelToMinuteRange(slotLabel, timeToMinutes) {
  if (!slotLabel || typeof timeToMinutes !== "function") return null;
  const parts = String(slotLabel)
    .split(" - ")
    .map((p) => p.trim());
  if (parts.length !== 2) return null;
  const start = timeToMinutes(parts[0]);
  const end = timeToMinutes(parts[1]);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return null;
  }
  return { start, end };
}

export const isSlotEffectivelyUnavailableForDentist = ({
  slot,
  dentistUid,
  dentistUnavailableSlots,
  hasUnavailabilityRecord,
  settings,
  recognizedSlots,
  timeToMinutes = null,
}) => {
  const slotsList = Array.isArray(dentistUnavailableSlots)
    ? dentistUnavailableSlots
    : [];
  if (slotsList.includes(slot)) {
    return true;
  }
  if (typeof timeToMinutes === "function") {
    const slotRange = slotLabelToMinuteRange(slot, timeToMinutes);
    if (slotRange) {
      for (const label of slotsList) {
        const blocked = slotLabelToMinuteRange(label, timeToMinutes);
        if (
          blocked &&
          slotRange.start < blocked.end &&
          blocked.start < slotRange.end
        ) {
          return true;
        }
      }
    }
  }
  if (!isChairCapacityConfigured(settings)) {
    return false;
  }
  if (isDentistOnRoster(dentistUid, settings)) {
    return false;
  }
  // Bench dentist with no swap data persisted yet -> treat as unavailable
  // by default so they don't show as freely bookable.
  if (!hasUnavailabilityRecord) {
    return true;
  }
  // Strict replaced-only model for bench dentists in chair mode:
  // - slot labels outside recognized per-day universe stay blocked
  // - labels inside the recognized universe are still blocked by default
  //   unless the persisted record explicitly opens them (slot omitted from
  //   a blocked-baseline array).
  if (Array.isArray(recognizedSlots) && recognizedSlots.length > 0) {
    if (!recognizedSlots.includes(slot)) {
      return true;
    }
    // Empty arrays cannot prove explicit openings; keep bench blocked.
    if (slotsList.length === 0) {
      return true;
    }
    // For bench records authored from blocked baseline, omitted slots are
    // explicit openings (swaps) and should be available.
    return slotsList.includes(slot);
  }
  // Bench dentist with a record where the slot is NOT listed means the slot
  // has been explicitly cleared (swapped in). Allow.
  return false;
};

// Compute the count of dentists currently available for a given slot on a
// date by walking the unavailableTimeSlots map. Excludes dentist UIDs that
// are not on the roster unless they have explicit swap-in (slot absent from
// their array but the array exists).
export const parseAppointmentRangeMinutes = (appointment, timeToMinutes) => {
  if (!appointment || typeof timeToMinutes !== "function") return null;
  if (!isBlockingAppointmentStatus(appointment.status)) {
    return null;
  }

  const startStr = appointment.startTime || appointment.time;
  const endStr = appointment.endTime;

  if (startStr && endStr) {
    const start = timeToMinutes(startStr);
    const end = timeToMinutes(endStr);
    if (
      typeof start !== "number" ||
      typeof end !== "number" ||
      Number.isNaN(start) ||
      Number.isNaN(end) ||
      end <= start
    ) {
      return null;
    }
    return { start, end };
  }

  if (startStr && !endStr) {
    const duration =
      typeof appointment.duration === "number" && appointment.duration > 0
        ? appointment.duration
        : 30;
    const start = timeToMinutes(startStr);
    if (typeof start !== "number" || Number.isNaN(start)) return null;
    return { start, end: start + duration };
  }

  if (!startStr && endStr) {
    const duration =
      typeof appointment.duration === "number" && appointment.duration > 0
        ? appointment.duration
        : 30;
    const end = timeToMinutes(endStr);
    if (typeof end !== "number" || Number.isNaN(end)) return null;
    return { start: end - duration, end };
  }

  return null;
};

/**
 * UIDs that jointly occupy chair timeline minutes for this appointment when a
 * single swap partner exists (shared inherited schedule).
 */
export const getLinkedTimelineOccupancyUids = ({
  appointment,
  swapsBySlot,
  settings,
}) => {
  const ids = new Set();
  if (!appointment || typeof appointment !== "object") return ids;
  if (!isBlockingAppointmentStatus(appointment.status)) {
    return ids;
  }
  const dentistId =
    typeof appointment.dentistId === "string" ? appointment.dentistId : null;
  if (!dentistId) return ids;
  ids.add(dentistId);
  if (!isChairCapacityConfigured(settings)) return ids;

  const swaps = swapsBySlot || {};
  if (isDentistOnRoster(dentistId, settings)) {
    const benches = getRosterPartnerBenchUidsForDay(swaps, dentistId);
    if (benches.length === 1) ids.add(benches[0]);
  } else {
    const rosters = getBenchPartnerRosterUidsForDay(swaps, dentistId);
    if (rosters.length === 1) ids.add(rosters[0]);
  }
  return ids;
};

export const appointmentOverlapsSlotLabel = ({
  appointment,
  slotLabel,
  timeToMinutes,
}) => {
  const slotRange = parseSlotLabelToRange(slotLabel, timeToMinutes);
  const apptRange = parseAppointmentRangeMinutes(appointment, timeToMinutes);
  if (!slotRange || !apptRange) return false;
  return rangesOverlap(slotRange, apptRange);
};

export const countAvailableDentistsForSlot = ({
  slot,
  unavailableTimeSlotsByDentist,
  knownDentistUids,
  settings,
  recognizedSlots,
  appointments = null,
  swapsBySlot = null,
  timeToMinutes = null,
}) => {
  if (!slot || !Array.isArray(knownDentistUids)) return 0;
  const lookup = unavailableTimeSlotsByDentist || {};
  const useApptOverlay =
    Array.isArray(appointments) &&
    appointments.length > 0 &&
    typeof timeToMinutes === "function" &&
    isChairCapacityConfigured(settings);

  let count = 0;
  knownDentistUids.forEach((uid) => {
    const entry = lookup[uid];
    const hasRecord = Array.isArray(entry);
    let blocked = isSlotEffectivelyUnavailableForDentist({
      slot,
      dentistUid: uid,
      dentistUnavailableSlots: hasRecord ? entry : [],
      hasUnavailabilityRecord: hasRecord,
      settings,
      recognizedSlots,
    });
    if (!blocked && useApptOverlay) {
      const slotRange = parseSlotLabelToRange(slot, timeToMinutes);
      if (slotRange && swapsBySlot != null) {
        for (let i = 0; i < appointments.length; i += 1) {
          const appt = appointments[i];
          const apptRange = parseAppointmentRangeMinutes(appt, timeToMinutes);
          if (!apptRange || !rangesOverlap(slotRange, apptRange)) continue;
          const occ = getLinkedTimelineOccupancyUids({
            appointment: appt,
            swapsBySlot,
            settings,
          });
          if (occ.has(uid)) {
            blocked = true;
            break;
          }
        }
      }
    }
    if (!blocked) count += 1;
  });
  return count;
};

// Validate Rule 1 for a planned save. `proposedChanges` is an object keyed by
// dentistUid -> { slot: string, willBeAvailable: boolean }[]. Returns an array
// of human-readable violation messages (empty when valid).
export const validateChairCapacity = ({
  slots,
  unavailableTimeSlotsByDentist,
  knownDentistUids,
  proposedChanges,
  settings,
  recognizedSlots,
  appointments,
  swapsBySlot,
  timeToMinutes,
}) => {
  if (!isChairCapacityConfigured(settings)) return [];
  if (!Array.isArray(slots) || slots.length === 0) return [];

  const projectedLookup = applyProposedChangesWithChairSemantics({
    unavailableTimeSlotsByDentist,
    proposedChanges,
    settings,
    slotUniverse: Array.isArray(recognizedSlots) && recognizedSlots.length > 0 ? recognizedSlots : slots,
  });

  const violations = [];
  slots.forEach((slot) => {
    const available = countAvailableDentistsForSlot({
      slot,
      unavailableTimeSlotsByDentist: projectedLookup,
      knownDentistUids,
      settings,
      recognizedSlots,
      appointments,
      swapsBySlot,
      timeToMinutes,
    });
    if (available > settings.chairCount) {
      violations.push(
        `Available dentists for ${slot} would be ${available}, exceeding chair capacity of ${settings.chairCount}.`
      );
    }
  });
  return violations;
};

// Apply a set of proposed per-slot availability edits using chair-aware defaults.
// Key behavior: for bench dentists with no existing record, initialize from a
// blocked baseline (slotUniverse) so opening one slot does not implicitly open
// every other slot.
export const applyProposedChangesWithChairSemantics = ({
  unavailableTimeSlotsByDentist,
  proposedChanges,
  settings,
  slotUniverse = [],
  forceBenchReseed = false,
}) => {
  const projectedLookup = JSON.parse(
    JSON.stringify(unavailableTimeSlotsByDentist || {})
  );

  Object.entries(proposedChanges || {}).forEach(([uid, edits]) => {
    const isBench =
      isChairCapacityConfigured(settings) && !isDentistOnRoster(uid, settings);
    const hasExistingArray = Array.isArray(projectedLookup[uid]);
    if (!hasExistingArray) {
      projectedLookup[uid] = isBench
        ? Array.from(new Set(slotUniverse || []))
        : [];
    } else if (isBench && forceBenchReseed) {
      const previousArray = projectedLookup[uid];
      const previousArraySet = new Set(previousArray);
      const universe = Array.isArray(slotUniverse) ? slotUniverse : [];
      const universeSet = new Set(universe);
      // Determine if previousArray is aligned with the current universe so we
      // can safely interpret label absence as "explicit opening". Otherwise
      // (stale data using a different slot universe), full re-seed without
      // preserving inferred openings.
      let overlap = 0;
      universeSet.forEach((slot) => {
        if (previousArraySet.has(slot)) overlap += 1;
      });
      const alignmentThreshold = Math.max(
        1,
        Math.ceil(universeSet.size / 2)
      );
      const isAlignedWithCurrentUniverse =
        universeSet.size > 0 && overlap >= alignmentThreshold;
      if (isAlignedWithCurrentUniverse) {
        const previouslyOpenedInUniverse = universe.filter(
          (slot) => !previousArraySet.has(slot)
        );
        const reseeded = Array.from(universeSet);
        previouslyOpenedInUniverse.forEach((slot) => {
          const idx = reseeded.indexOf(slot);
          if (idx >= 0) reseeded.splice(idx, 1);
        });
        projectedLookup[uid] = reseeded;
      } else {
        projectedLookup[uid] = Array.from(universeSet);
      }
    }
    edits.forEach(({ slot, willBeAvailable }) => {
      const arr = projectedLookup[uid];
      if (willBeAvailable) {
        const idx = arr.indexOf(slot);
        if (idx >= 0) arr.splice(idx, 1);
      } else if (!arr.includes(slot)) {
        arr.push(slot);
      }
    });
  });

  return projectedLookup;
};

// Detect Rule 3 conflicts: a dentist must not be marked more unavailable for a
// slot during which they have an active patient appointment.
// `appointmentsForDate` is an array of appointment objects (already filtered
// to a single date). The function returns an array of { dentistUid, slot,
// appointment } records.
export const findAppointmentConflicts = ({
  proposedChanges,
  appointmentsForDate,
  timeToMinutes,
  slotToMinutes,
}) => {
  const conflicts = [];
  if (!proposedChanges || !Array.isArray(appointmentsForDate)) return conflicts;

  Object.entries(proposedChanges).forEach(([dentistUid, edits]) => {
    edits.forEach(({ slot, willBeAvailable }) => {
      if (willBeAvailable) return;
      const slotRange = slotToMinutes
        ? slotToMinutes(slot)
        : parseSlotRange(slot, timeToMinutes);
      if (!slotRange) return;
      appointmentsForDate.forEach((appt) => {
        if (!appt) return;
        const apptDentistId =
          appt.dentistId || appt.dentistUID || appt.dentistUid;
        if (apptDentistId !== dentistUid) return;
        if (!isBlockingAppointmentStatus(appt.status)) return;
        const apptStart = timeToMinutes
          ? timeToMinutes(appt.startTime || appt.time)
          : 0;
        const apptEnd = appt.endTime
          ? timeToMinutes
            ? timeToMinutes(appt.endTime)
            : 0
          : apptStart + (appt.duration || 30);
        const overlaps =
          slotRange.start < apptEnd && slotRange.end > apptStart;
        if (overlaps) {
          conflicts.push({ dentistUid, slot, appointment: appt });
        }
      });
    });
  });
  return conflicts;
};

// Best-effort parse of slot label like "9:00 AM - 9:30 AM" into minute
// integers. Falls back to null when format is unexpected.
export const parseSlotRange = (slot, timeToMinutes) => {
  if (!slot || typeof slot !== "string") return null;
  const parts = slot.split(" - ");
  if (parts.length !== 2 || !timeToMinutes) return null;
  const start = timeToMinutes(parts[0].trim());
  const end = timeToMinutes(parts[1].trim());
  if (
    typeof start !== "number" ||
    typeof end !== "number" ||
    Number.isNaN(start) ||
    Number.isNaN(end)
  ) {
    return null;
  }
  return { start, end };
};

// Convenience: returns the set of bench dentist UIDs given a settings object
// and the full known dentist list.
export const getBenchDentistUids = (knownDentistUids, settings) => {
  if (!Array.isArray(knownDentistUids)) return [];
  if (!settings || !Array.isArray(settings.rosterDentistIds)) {
    return [];
  }
  const roster = new Set(settings.rosterDentistIds);
  return knownDentistUids.filter((uid) => !roster.has(uid));
};

// Convenience: dentists currently free in a slot (for the Removal modal
// dropdown). Only roster dentists that are NOT in their per-day unavailable
// array qualify.
export const getRosterDentistsAvailableForSlot = ({
  slot,
  unavailableTimeSlotsByDentist,
  settings,
}) => {
  if (!isChairCapacityConfigured(settings)) return [];
  const lookup = unavailableTimeSlotsByDentist || {};
  return settings.rosterDentistIds.filter((uid) => {
    const entry = lookup[uid];
    if (!Array.isArray(entry)) return true;
    return !entry.includes(slot);
  });
};

// Convenience: bench dentists eligible to take over a slot (for the
// Replacement modal dropdown).
export const getBenchDentistsAvailableForSlot = ({
  slot,
  knownDentistUids,
  unavailableTimeSlotsByDentist,
  settings,
}) => {
  const bench = getBenchDentistUids(knownDentistUids, settings);
  if (bench.length === 0) return [];
  const lookup = unavailableTimeSlotsByDentist || {};
  return bench.filter((uid) => {
    const entry = lookup[uid];
    if (!Array.isArray(entry)) return true;
    return !entry.includes(slot);
  });
};

// Convenience: bench dentists eligible to be selected as replacement candidates
// for a slot. These are bench dentists currently effectively blocked for the
// slot (default bench-blocked or explicitly unavailable), because the
// replacement flow is meant to swap them in.
export const getBenchDentistsBlockedForSlot = ({
  slot,
  knownDentistUids,
  unavailableTimeSlotsByDentist,
  settings,
}) => {
  const bench = getBenchDentistUids(knownDentistUids, settings);
  if (bench.length === 0) return [];
  const lookup = unavailableTimeSlotsByDentist || {};
  return bench.filter((uid) => {
    const entry = lookup[uid];
    const hasRecord = Array.isArray(entry);
    return isSlotEffectivelyUnavailableForDentist({
      slot,
      dentistUid: uid,
      dentistUnavailableSlots: hasRecord ? entry : [],
      hasUnavailabilityRecord: hasRecord,
      settings,
    });
  });
};

// Booking dropdown visibility helper.
// If chair config is enabled, dentists are shown when:
// - they are on roster, OR
// - they are bench dentists with at least one effectively available slot
//   for the chosen date.
export const isDentistVisibleForBookingDropdown = ({
  dentistUid,
  settings,
  unavailableTimeSlotsByDentist,
  daySlots,
}) => {
  if (!dentistUid) return false;
  if (!isChairCapacityConfigured(settings)) return true;
  if (isDentistOnRoster(dentistUid, settings)) return true;
  if (!Array.isArray(daySlots) || daySlots.length === 0) return false;

  const lookup = unavailableTimeSlotsByDentist || {};
  const entry = lookup[dentistUid];
  const hasRecord = Array.isArray(entry);
  const dentistSlots = hasRecord ? entry : [];

  return daySlots.some(
    (slot) =>
      !isSlotEffectivelyUnavailableForDentist({
        slot,
        dentistUid,
        dentistUnavailableSlots: dentistSlots,
        hasUnavailabilityRecord: hasRecord,
        settings,
      })
  );
};

// Normalize `chairSwaps/{date}` document into a stable object:
// {
//   [swapKey]: { slot: string, fromRosterUid: string, toBenchUid: string }
// }
// swapKey is roster-scoped so multiple rosters can assign the same slot label
// on the same date without overriding each other.
export const normalizeChairSwaps = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  const slotsSource =
    raw && typeof raw.slots === "object" && raw.slots !== null ? raw.slots : raw;
  const normalized = {};
  Object.entries(slotsSource).forEach(([rawKey, value], idx) => {
    if (!rawKey || typeof rawKey !== "string") return;
    if (!value || typeof value !== "object") return;
    const inferredSlotFromKey = rawKey.includes("::")
      ? rawKey.split("::").slice(1).join("::").trim()
      : rawKey;
    const fromRosterUid =
      typeof value.fromRosterUid === "string" ? value.fromRosterUid : null;
    const toBenchUid =
      typeof value.toBenchUid === "string" ? value.toBenchUid : null;
    const slot =
      typeof value.slot === "string" && value.slot.trim()
        ? value.slot.trim()
        : inferredSlotFromKey;
    if (!fromRosterUid || !toBenchUid) return;
    if (!slot) return;
    const preferredKey = `${fromRosterUid}::${slot}`;
    const stableKey = normalized[preferredKey]
      ? `${preferredKey}::${idx}`
      : preferredKey;
    normalized[stableKey] = { slot, fromRosterUid, toBenchUid };
  });
  return normalized;
};

/** All roster UIDs paired with this bench via any swap row on this date. */
export const getBenchPartnerRosterUidsForDay = (swapsBySlot, benchUid) => {
  if (!benchUid) return [];
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  return Array.from(
    new Set(
      Object.values(normalized)
        .filter((entry) => entry.toBenchUid === benchUid && entry.fromRosterUid)
        .map((entry) => entry.fromRosterUid)
    )
  );
};

/** All bench UIDs paired with this roster dentist via any swap row on this date. */
export const getRosterPartnerBenchUidsForDay = (swapsBySlot, rosterUid) => {
  if (!rosterUid) return [];
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  return Array.from(
    new Set(
      Object.values(normalized)
        .filter((entry) => entry.fromRosterUid === rosterUid && entry.toBenchUid)
        .map((entry) => entry.toBenchUid)
    )
  );
};

/** When exactly one roster shares swaps with this bench, return that UID; else null. */
export const getCanonicalSwapPartnerForBench = (swapsBySlot, benchUid) => {
  const partners = getBenchPartnerRosterUidsForDay(swapsBySlot, benchUid);
  return partners.length === 1 ? partners[0] : null;
};

export const getBenchSwapSlots = ({ swapsBySlot, benchUid }) => {
  if (!benchUid) return [];
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  return Array.from(
    new Set(
      Object.values(normalized)
        .filter((value) => value.toBenchUid === benchUid)
        .map((value) => value.slot)
        .filter(Boolean)
    )
  );
};

export const getSwappedInBenchUids = ({ swapsBySlot }) => {
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  return Array.from(
    new Set(
      Object.values(normalized)
        .map((value) => value.toBenchUid)
        .filter(Boolean)
    )
  );
};

export const getLinkedBenchForRosterSlot = ({ swapsBySlot, rosterUid, slot }) => {
  if (!rosterUid || !slot) return null;
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  const match = Object.values(normalized).find(
    (entry) => entry.slot === slot && entry.fromRosterUid === rosterUid
  );
  return match?.toBenchUid || null;
};

export const getLinkedRosterForBenchSlot = ({ swapsBySlot, benchUid, slot }) => {
  if (!benchUid || !slot) return null;
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  const match = Object.values(normalized).find(
    (entry) => entry.slot === slot && entry.toBenchUid === benchUid
  );
  return match?.fromRosterUid || null;
};

export const upsertSwapForSlot = ({
  swapsBySlot,
  slot,
  fromRosterUid,
  toBenchUid,
}) => {
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  if (!slot || !fromRosterUid || !toBenchUid) return normalized;
  const next = { ...normalized };
  Object.entries(next).forEach(([key, value]) => {
    if (value?.slot === slot && value?.fromRosterUid === fromRosterUid) {
      delete next[key];
    }
  });
  const swapKey = `${fromRosterUid}::${slot}`;
  return {
    ...next,
    [swapKey]: { slot, fromRosterUid, toBenchUid },
  };
};

export const removeSwapForSlot = ({
  swapsBySlot,
  slot,
  fromRosterUid = null,
  toBenchUid = null,
}) => {
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  if (!slot) return normalized;
  const next = { ...normalized };
  let removed = false;
  Object.entries(next).forEach(([key, value]) => {
    if (!value || value.slot !== slot) return;
    if (fromRosterUid && value.fromRosterUid !== fromRosterUid) return;
    if (toBenchUid && value.toBenchUid !== toBenchUid) return;
    delete next[key];
    removed = true;
  });
  if (!removed) return normalized;
  return next;
};

export const parseSlotLabelToRange = (slotLabel, timeToMinutes) => {
  if (!slotLabel || typeof slotLabel !== "string" || !timeToMinutes) return null;
  const [startRaw, endRaw] = slotLabel.split(" - ").map((part) => part.trim());
  if (!startRaw || !endRaw) return null;
  const start = timeToMinutes(startRaw);
  const end = timeToMinutes(endRaw);
  if (
    typeof start !== "number" ||
    typeof end !== "number" ||
    Number.isNaN(start) ||
    Number.isNaN(end) ||
    end <= start
  ) {
    return null;
  }
  return { start, end };
};

export const mergeSlotRanges = (ranges = []) => {
  const sorted = (Array.isArray(ranges) ? ranges : [])
    .filter(
      (range) =>
        range &&
        typeof range.start === "number" &&
        typeof range.end === "number" &&
        !Number.isNaN(range.start) &&
        !Number.isNaN(range.end) &&
        range.end > range.start
    )
    .sort((a, b) => a.start - b.start);
  return sorted.reduce((acc, next) => {
    if (acc.length === 0) return [{ ...next }];
    const last = acc[acc.length - 1];
    if (next.start <= last.end) {
      last.end = Math.max(last.end, next.end);
      return acc;
    }
    acc.push({ ...next });
    return acc;
  }, []);
};

export const rangesOverlap = (a, b) => {
  if (!a || !b) return false;
  return a.start < b.end && a.end > b.start;
};

export const getBenchSwapCoverageRanges = ({
  swapsBySlot,
  benchUid,
  timeToMinutes,
}) => {
  if (!benchUid || !timeToMinutes) return [];
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  const ranges = Object.entries(normalized)
    .filter(([, value]) => value.toBenchUid === benchUid)
    .map(([, value]) => parseSlotLabelToRange(value.slot, timeToMinutes))
    .filter(Boolean);
  return mergeSlotRanges(ranges);
};

/** Merged minute ranges for slots this roster dentist swapped away to a bench (assigned roster tail boundary). */
export const getRosterSwapAwayCoverageRanges = ({
  swapsBySlot,
  rosterUid,
  timeToMinutes,
}) => {
  if (!rosterUid || !timeToMinutes) return [];
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  const ranges = Object.entries(normalized)
    .filter(([, value]) => value.fromRosterUid === rosterUid)
    .map(([, value]) => parseSlotLabelToRange(value.slot, timeToMinutes))
    .filter(Boolean);
  return mergeSlotRanges(ranges);
};

/**
 * Roster patient-assignable minutes: office-day start through either calendar end (11:59 PM) when
 * this roster dentist has swapped nothing away (full day on-chair), or through default office
 * close (6:00 PM) when swaps exist—then subtract swap-away ranges (e.g. afternoon to bench).
 */
export const getRosterAssignablePatientCoverageRanges = ({
  swapsBySlot,
  rosterUid,
  timeToMinutes,
  officeDayStartMinutes = 9 * 60,
  officeDayEndMinutes,
}) => {
  if (!rosterUid || !timeToMinutes) return [];
  const CALENDAR_DAY_END_MINUTES = 23 * 60 + 59;
  const swapAway = getRosterSwapAwayCoverageRanges({
    swapsBySlot,
    rosterUid,
    timeToMinutes,
  });
  const resolvedOfficeEnd =
    typeof officeDayEndMinutes === "number" && !Number.isNaN(officeDayEndMinutes)
      ? officeDayEndMinutes
      : swapAway.length === 0
        ? CALENDAR_DAY_END_MINUTES
        : 18 * 60;
  if (
    typeof officeDayStartMinutes !== "number" ||
    typeof resolvedOfficeEnd !== "number" ||
    Number.isNaN(officeDayStartMinutes) ||
    Number.isNaN(resolvedOfficeEnd) ||
    resolvedOfficeEnd <= officeDayStartMinutes
  ) {
    return [];
  }
  let pieces = [{ start: officeDayStartMinutes, end: resolvedOfficeEnd }];
  for (const sub of swapAway) {
    const next = [];
    for (const p of pieces) {
      if (p.end <= sub.start || p.start >= sub.end) {
        next.push(p);
      } else {
        if (p.start < sub.start) next.push({ start: p.start, end: sub.start });
        if (p.end > sub.end) next.push({ start: sub.end, end: p.end });
      }
    }
    pieces = next.filter((x) => x.end > x.start);
  }
  return mergeSlotRanges(pieces);
};

/** True if [range.start, range.end] lies entirely inside the union of disjoint merged assignable intervals (sorted). */
export const minuteRangeFullyContainedInUnion = (range, sortedMergedAssignable) => {
  if (
    !range ||
    typeof range.start !== "number" ||
    typeof range.end !== "number" ||
    Number.isNaN(range.start) ||
    Number.isNaN(range.end) ||
    range.end <= range.start ||
    !Array.isArray(sortedMergedAssignable) ||
    sortedMergedAssignable.length === 0
  ) {
    return false;
  }
  let cur = range.start;
  const targetEnd = range.end;
  for (let i = 0; i < sortedMergedAssignable.length; i += 1) {
    const seg = sortedMergedAssignable[i];
    if (!seg || typeof seg.start !== "number" || typeof seg.end !== "number") continue;
    if (seg.end <= cur) continue;
    if (seg.start > cur) return false;
    cur = Math.min(targetEnd, seg.end);
    if (cur >= targetEnd) return true;
  }
  return false;
};

/**
 * True when `slotLabel` is fully contained in merged contiguous swap windows for a bench
 * dentist — matches PatientBooking bench filler geometry (`doesSlotOverlapSwapSlots`).
 * Used for roster mirror when bench has no `unavailableTimeSlots` row (swap-open baseline).
 */
export const benchPatientSlotContainedInMergedSwapWindows = ({
  slotLabel,
  swapsBySlot,
  benchUid,
  timeToMinutes,
}) => {
  const swapSlotLabels = getBenchSwapSlots({ swapsBySlot, benchUid });
  if (
    !slotLabel ||
    typeof slotLabel !== "string" ||
    !Array.isArray(swapSlotLabels) ||
    swapSlotLabels.length === 0 ||
    typeof timeToMinutes !== "function"
  ) {
    return false;
  }
  const source = parseSlotLabelToRange(slotLabel, timeToMinutes);
  if (!source) return false;
  const mergedWindows = mergeSlotRanges(
    swapSlotLabels
      .map((label) => parseSlotLabelToRange(label, timeToMinutes))
      .filter(Boolean)
  );
  return mergedWindows.some(
    (target) => source.start >= target.start && source.end <= target.end
  );
};

/** Shared bench “would patient booking show bookable here?” predicate for roster mirror / handoff. */
export const benchShowsPatientBookableForRosterMirror = ({
  benchUid,
  slotLabel,
  slotRange,
  timeToMinutes,
  chairSettings,
  swapsBySlot,
  unavailableSlotsByDentistForDate,
  appointmentMinuteSlots,
  recognizedSlotsExtras,
}) => {
  if (!benchUid || !slotRange || typeof timeToMinutes !== "function") {
    return false;
  }
  const coverageRanges = getBenchSwapCoverageRanges({
    swapsBySlot,
    benchUid,
    timeToMinutes,
  });
  if (!coverageRanges.some((r) => rangesOverlap(slotRange, r))) {
    return false;
  }
  const overlapsBenchOwnedAppointment = (appointmentMinuteSlots || []).some(
    (s) => {
      if (s.appointment?.dentistId !== benchUid) return false;
      return rangesOverlap(slotRange, { start: s.start, end: s.end });
    }
  );
  if (overlapsBenchOwnedAppointment) return false;

  const mirrorBenchSlotsMap = unavailableSlotsByDentistForDate[benchUid];
  const hasMirrorBenchRecord = Array.isArray(mirrorBenchSlotsMap);
  if (!hasMirrorBenchRecord) {
    return benchPatientSlotContainedInMergedSwapWindows({
      slotLabel,
      swapsBySlot,
      benchUid,
      timeToMinutes,
    });
  }
  const extras =
    Array.isArray(recognizedSlotsExtras) && recognizedSlotsExtras.length > 0
      ? recognizedSlotsExtras
      : buildStandardHalfHourSlotLabels({});
  const blockedOnBenchGrid = isSlotEffectivelyUnavailableForDentist({
    slot: slotLabel,
    dentistUid: benchUid,
    dentistUnavailableSlots: mirrorBenchSlotsMap,
    hasUnavailabilityRecord: true,
    settings: chairSettings,
    recognizedSlots: [slotLabel, ...extras],
  });
  return !blockedOnBenchGrid;
};

/**
 * True when a roster dentist’s empty slot should match PatientBooking: unavailable because a
 * linked bench dentist would show that slice as patient-bookable (swap mirror).
 */
export const rosterPatientSlotUnavailableDueToBenchMirror = ({
  rosterUid,
  slotLabel,
  timeToMinutes,
  chairSettings,
  swapsBySlot,
  unavailableSlotsByDentistForDate = {},
  appointmentsSameDay = [],
  recognizedSlotsExtras,
}) => {
  if (!slotLabel || typeof slotLabel !== "string" || typeof timeToMinutes !== "function") {
    return false;
  }
  if (!isChairCapacityConfigured(chairSettings)) return false;
  if (!isDentistOnRoster(rosterUid, chairSettings)) return false;
  const slotRange = parseSlotLabelToRange(slotLabel, timeToMinutes);
  if (!slotRange) return false;
  const linkedBenchUids = getRosterPartnerBenchUidsForDay(swapsBySlot, rosterUid);
  if (linkedBenchUids.length === 0) return false;

  const appointmentMinuteSlots = [];
  for (const appt of appointmentsSameDay || []) {
    const range = parseAppointmentRangeMinutes(appt, timeToMinutes);
    if (!range) continue;
    appointmentMinuteSlots.push({
      start: range.start,
      end: range.end,
      appointment: appt,
    });
  }

  return linkedBenchUids.some((benchUid) =>
    benchShowsPatientBookableForRosterMirror({
      benchUid,
      slotLabel,
      slotRange,
      timeToMinutes,
      chairSettings,
      swapsBySlot,
      unavailableSlotsByDentistForDate,
      appointmentMinuteSlots,
      recognizedSlotsExtras,
    })
  );
};

export const getLinkedRosterUidsForBenchRange = ({
  swapsBySlot,
  benchUid,
  slotLabel,
  timeToMinutes,
}) => {
  if (!benchUid || !slotLabel || !timeToMinutes) return [];
  const sourceRange = parseSlotLabelToRange(slotLabel, timeToMinutes);
  if (!sourceRange) return [];
  const normalized = normalizeChairSwaps(swapsBySlot || {});
  return Array.from(
    new Set(
      Object.entries(normalized)
        .filter(([, value]) => value.toBenchUid === benchUid && value.fromRosterUid)
        .filter(([, value]) =>
          rangesOverlap(sourceRange, parseSlotLabelToRange(value.slot, timeToMinutes))
        )
        .map(([, value]) => value.fromRosterUid)
    )
  );
};

/** 9:00 AM–6:00 PM half-hour labels (same shape as Patient / Manage calendars). */
export const buildStandardHalfHourSlotLabels = ({
  officeStartMinutes = 9 * 60,
  officeEndMinutes = 18 * 60,
  slotIntervalMinutes = 30,
} = {}) => {
  const labels = [];
  for (
    let t = officeStartMinutes;
    t < officeEndMinutes;
    t += slotIntervalMinutes
  ) {
    const end = Math.min(t + slotIntervalMinutes, officeEndMinutes);
    if (end - t < 20) continue;
    const fmt = (minutes) => {
      const hours24 = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const ampm = hours24 >= 12 ? "PM" : "AM";
      const hrs12 = hours24 % 12 || 12;
      return `${hrs12}:${mins.toString().padStart(2, "0")} ${ampm}`;
    };
    labels.push(`${fmt(t)} - ${fmt(end)}`);
  }
  return labels;
};

/**
 * True when a roster dentist's empty calendar gap should stay unavailable because it is
 * still inside the linked bench dentist's inherited swap timeline (aligned with
 * PatientAppointmentBooking roster filler logic).
 */
export const rosterGapOverlapsLinkedBenchInheritedExclusive = ({
  rosterUid,
  slotRange,
  slotLabel: _slotLabelIgnored,
  timeToMinutes,
  chairSettings,
  swapsBySlot,
  unavailableSlotsByDentistForDate = {},
  appointmentsSameDay = [],
  recognizedSlotsExtras,
  officeStartMinutes = 9 * 60,
  officeEndMinutes = 18 * 60,
  slotIntervalMinutes = 30,
}) => {
  if (!rosterUid || !slotRange || typeof timeToMinutes !== "function") {
    return false;
  }
  if (!isChairCapacityConfigured(chairSettings)) return false;
  if (!isDentistOnRoster(rosterUid, chairSettings)) return false;

  const linkedBenchUidsForRoster = getRosterPartnerBenchUidsForDay(
    swapsBySlot,
    rosterUid
  );
  if (linkedBenchUidsForRoster.length === 0) return false;

  const extras =
    Array.isArray(recognizedSlotsExtras) && recognizedSlotsExtras.length > 0
      ? recognizedSlotsExtras
      : buildStandardHalfHourSlotLabels({
          officeStartMinutes,
          officeEndMinutes,
          slotIntervalMinutes,
        });

  const appointmentSlots = [];
  for (const appt of appointmentsSameDay) {
    const range = parseAppointmentRangeMinutes(appt, timeToMinutes);
    if (!range) continue;
    appointmentSlots.push({
      start: range.start,
      end: range.end,
      appointment: appt,
    });
  }
  appointmentSlots.sort((a, b) => a.start - b.start);

  let maxBenchLinkedAppointmentEnd = 0;
  appointmentSlots.forEach((s) => {
    const id = s.appointment?.dentistId;
    if (id && linkedBenchUidsForRoster.includes(id)) {
      maxBenchLinkedAppointmentEnd = Math.max(maxBenchLinkedAppointmentEnd, s.end);
    }
  });

  let rosterBenchHandoffMinute = officeEndMinutes;
  if (linkedBenchUidsForRoster.length > 0 && maxBenchLinkedAppointmentEnd > 0) {
    let cur = maxBenchLinkedAppointmentEnd;
    while (cur < officeEndMinutes) {
      const segEnd = Math.min(cur + slotIntervalMinutes, officeEndMinutes);
      const slotDuration = segEnd - cur;
      if (slotDuration < 20) break;

      const startHours = Math.floor(cur / 60);
      const startMinutesRem = cur % 60;
      const startAmpm = startHours >= 12 ? "PM" : "AM";
      const startDisplayHours = startHours % 12 || 12;
      const startTimeStr = `${startDisplayHours}:${startMinutesRem
        .toString()
        .padStart(2, "0")} ${startAmpm}`;
      const endHours = Math.floor(segEnd / 60);
      const endMinutesRem = segEnd % 60;
      const endAmpm = endHours >= 12 ? "PM" : "AM";
      const endDisplayHours = endHours % 12 || 12;
      const endTimeStr = `${endDisplayHours}:${endMinutesRem
        .toString()
        .padStart(2, "0")} ${endAmpm}`;
      const segLabel = `${startTimeStr} - ${endTimeStr}`;
      const segRange = { start: cur, end: segEnd };

      const overlapsAnyBenchLinkedAppointment = appointmentSlots.some(
        (s) =>
          linkedBenchUidsForRoster.includes(s.appointment?.dentistId) &&
          rangesOverlap(segRange, { start: s.start, end: s.end })
      );

      if (!overlapsAnyBenchLinkedAppointment) {
        const bookable = linkedBenchUidsForRoster.some((benchUid) =>
          benchShowsPatientBookableForRosterMirror({
            benchUid,
            slotLabel: segLabel,
            slotRange: segRange,
            timeToMinutes,
            chairSettings,
            swapsBySlot,
            unavailableSlotsByDentistForDate,
            appointmentMinuteSlots: appointmentSlots,
            recognizedSlotsExtras: extras,
          })
        );
        if (!bookable) {
          const overlapsLinkedBenchCoverage = linkedBenchUidsForRoster.some(
            (benchUid) => {
              const cov = getBenchSwapCoverageRanges({
                swapsBySlot,
                benchUid,
                timeToMinutes,
              });
              return cov.some((r) => rangesOverlap(segRange, r));
            }
          );
          rosterBenchHandoffMinute = overlapsLinkedBenchCoverage ? segEnd : cur;
          break;
        }
      }
      cur = segEnd;
    }
  }

  return (
    linkedBenchUidsForRoster.length > 0 &&
    maxBenchLinkedAppointmentEnd > 0 &&
    rosterBenchHandoffMinute > maxBenchLinkedAppointmentEnd &&
    slotRange.start < rosterBenchHandoffMinute &&
    slotRange.end > maxBenchLinkedAppointmentEnd
  );
};
